import {
    Activity,
    ChevronDown,
    Database,
    KeyRound,
    LineChart,
    Pencil,
    Play,
    RefreshCw,
    Timer,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import BONavigation from "../../../components/admin/BONavigation";
import EventDetailModal from "../../events/EventDetailModal";
import PersonDetailModal from "./PersonDetailModal";
import { getEvent, triggerEnrichment } from "../../../interfaces/events";
import {
    getServicesOverview,
    forceServicesSnapshot,
    toggleService,
    runService,
    updateGlobalServiceSettings,
    updateServiceSettings,
    getMigrations,
    updateApiKey,
    getServiceRecentDocs,
    ServicesOverview,
    ServiceOverviewItem,
    ServiceHealth,
    MigrationInfo,
    TrackedApiService,
    RecentDocPreview,
    ApiKeyInfo,
    ApiKeyPacing,
    PacingDay,
} from "../../../interfaces/servicesAdmin";

// ─── Formatting helpers ────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined): string =>
    n === null || n === undefined ? "—" : n.toLocaleString("fr-FR");

/** Montant en dollars — 2 décimales, 4 sous le cent pour ne pas afficher "0,00 $". */
const fmtUsd = (n: number | null | undefined): string => {
    if (n === null || n === undefined) return "—";
    const digits = n !== 0 && Math.abs(n) < 0.01 ? 4 : 2;
    return `${n.toLocaleString("fr-FR", { minimumFractionDigits: digits, maximumFractionDigits: digits })} $`;
};

/** Prix unitaire d'un appel — toujours 4 décimales ($ 0,0015). */
const fmtUnitUsd = (n: number): string =>
    `${n.toLocaleString("fr-FR", { minimumFractionDigits: 4, maximumFractionDigits: 6 })} $`;

function timeAgo(dateStr: string | null): { text: string; color: string } {
    if (!dateStr) return { text: "jamais", color: "text-red-500" };
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const diffSec = Math.floor(diffMs / 1_000);
    const diffMin = Math.floor(diffMs / 60_000);
    const diffH = Math.floor(diffMs / 3_600_000);
    if (diffSec < 60) return { text: `il y a ${diffSec} s`, color: "text-green-600" };
    if (diffH < 1) return { text: `il y a ${diffMin} min`, color: "text-green-600" };
    if (diffH < 24) return { text: `il y a ${diffH} h`, color: "text-amber-600" };
    return { text: `il y a ${Math.floor(diffH / 24)} j`, color: "text-red-500" };
}

function fmtSpeed(perHour: number | null): string {
    if (perHour === null) return "—";
    if (perHour >= 10) return `${Math.round(perHour).toLocaleString("fr-FR")}/h`;
    return `${perHour.toFixed(1).replace(".", ",")}/h`;
}

function fmtEta(hours: number | null, blocked: boolean): { text: string; color: string } {
    if (blocked) return { text: "∞ — arrivées ≥ débit", color: "text-red-500" };
    if (hours === null) return { text: "—", color: "text-gray-400" };
    if (hours < 1) return { text: "< 1 h", color: "text-green-600" };
    if (hours < 48) return { text: `~${Math.round(hours)} h`, color: "text-green-600" };
    const days = hours / 24;
    if (days < 60) return { text: `~${Math.round(days)} j`, color: "text-amber-600" };
    return { text: `~${Math.round(days)} j`, color: "text-red-500" };
}

function fmtCycle(days: number | null): string {
    if (days === null) return "—";
    if (days < 2) return `~${Math.round(days * 24)} h`;
    if (days < 90) return `~${Math.round(days)} j`;
    return `~${(days / 30).toFixed(1).replace(".", ",")} mois`;
}

function fmtDuration(ms: number | null): string {
    if (ms === null) return "—";
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
    return `${Math.round(ms / 60_000)} min`;
}

const PERIOD_LABELS: Record<string, string> = { DAY: "par jour", WEEK: "par semaine", MONTH: "par mois" };

// Registry key → detail-page type (existing routes /bo/services/:type)
const DETAIL_TYPE: Record<string, string> = {
    closingChecker: "closing_checker",
    bodaccHistory: "bodaccHistory",
    aiConfidence: "aiConfidencePending",
    "rule:google_place_DFS": "google_place_DFS",
    "rule:website_content_OTS": "website_content_OTS",
    "rule:linkedin": "linkedin",
    "rule:google_place": "google_place",
};

// Logs are shipped to Better Stack via Logtail (see crm-back/src/logs/logger.ts).
// If your team's live-tail URL differs (telemetry.betterstack.com/team/<id>/tail),
// set it here — the ?q= filter on the `service` field is appended automatically.
const BETTERSTACK_TAIL_BASE = "https://telemetry.betterstack.com/tail";

function logsUrl(logService: string | null): string | null {
    if (!logService) return null;
    return `${BETTERSTACK_TAIL_BASE}?q=${encodeURIComponent(`service="${logService}"`)}`;
}

// ─── Small building blocks ─────────────────────────────────────────────────────

const HEALTH_STYLE: Record<ServiceHealth, { label: string; cls: string }> = {
    running: { label: "En marche", cls: "bg-green-50 text-green-700 border-green-200" },
    idle: { label: "À jour", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    stalled: { label: "Bloqué", cls: "bg-red-50 text-red-700 border-red-200" },
    disabled: { label: "Inactif", cls: "bg-gray-100 text-gray-500 border-gray-200" },
    retired: { label: "Retiré", cls: "bg-gray-100 text-gray-400 border-gray-200" },
};

function HealthBadge({ health }: { health: ServiceHealth }) {
    const s = HEALTH_STYLE[health];
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${s.cls}`}>
            {s.label}
        </span>
    );
}

function ToggleSwitch({
    enabled,
    disabled,
    onChange,
}: {
    enabled: boolean;
    disabled?: boolean;
    onChange: (v: boolean) => void;
}) {
    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onChange(!enabled);
            }}
            disabled={disabled}
            title={enabled ? "Désactiver le service" : "Activer le service"}
            className={`relative inline-flex h-4.5 w-8 shrink-0 items-center rounded-full transition-colors ${
                enabled ? "bg-green-500" : "bg-gray-300"
            } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
            style={{ height: 18, width: 32 }}
        >
            <span
                className="inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform"
                style={{ transform: enabled ? "translateX(15px)" : "translateX(2px)", height: 14, width: 14 }}
            />
        </button>
    );
}

function Sparkline({ points, color }: { points: { value: number }[]; color: string }) {
    if (points.length < 2) return null;
    const w = 120;
    const h = 28;
    const values = points.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const path = values
        .map((v, i) => {
            const x = (i / (values.length - 1)) * w;
            const y = h - 3 - ((v - min) / range) * (h - 6);
            return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ");
    const trendUp = values[values.length - 1] > values[0];
    return (
        <div className="flex items-center gap-1.5" title="Backlog sur 24 h">
            <svg width={w} height={h} className="shrink-0">
                <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
            </svg>
            <span className={`text-xs ${trendUp ? "text-red-400" : "text-green-500"}`}>
                {trendUp ? "↗" : "↘"}
            </span>
        </div>
    );
}

function Chip({ children, tone = "gray" }: { children: React.ReactNode; tone?: "gray" | "amber" | "blue" }) {
    const cls =
        tone === "amber"
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : tone === "blue"
            ? "bg-blue-50 text-blue-600 border-blue-200"
            : "bg-gray-50 text-gray-500 border-gray-200";
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] border ${cls}`}>{children}</span>
    );
}

// ─── Service card ──────────────────────────────────────────────────────────────

// Fetches a single Event and renders it via the existing EventDetailModal
// (same enrichment/options viewer used on the main Events admin page).
function EventDrillDownModal({ eventId, onClose }: { eventId: string; onClose: () => void }) {
    const [event, setEvent] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getEvent(eventId);
            setEvent(res.data);
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading || !event) {
        return createPortal(
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg px-6 py-4 text-sm text-gray-500">Chargement…</div>
            </div>,
            document.body
        );
    }

    return (
        <EventDetailModal
            event={event}
            onClose={onClose}
            onTriggerEnrichment={async (id, type) => {
                await triggerEnrichment(id, type);
                await load();
            }}
            onRefresh={load}
        />
    );
}

function RecentActivityList({ svcKey }: { svcKey: string }) {
    const [docs, setDocs] = useState<RecentDocPreview[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<RecentDocPreview | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getServiceRecentDocs(svcKey)
            .then((d) => alive && setDocs(d))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [svcKey]);

    return (
        <div onClick={(e) => e.stopPropagation()}>
            {loading ? (
                <p className="text-xs text-gray-400">Chargement…</p>
            ) : docs && docs.length > 0 ? (
                <div className="border border-gray-100 rounded overflow-hidden">
                    {docs.map((d) => (
                        <button
                            key={d._id}
                            onClick={() => (d.model === "Establishment" ? window.open(`/establishments/${d._id}`, "_blank") : setSelected(d))}
                            className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs hover:bg-[#F9F6F3] border-b border-gray-100 last:border-b-0 text-left"
                        >
                            <span className="truncate flex-1 text-gray-800">{d.label}</span>
                            {d.status && (
                                <span className={`shrink-0 px-1.5 py-0.5 text-[10px] rounded-sm border ${getStatusTone(d.status)}`}>
                                    {d.status}
                                </span>
                            )}
                            {!!d.optionsCount && (
                                <span className="shrink-0 text-gray-400">
                                    {d.optionsCount} option{d.optionsCount > 1 ? "s" : ""}
                                </span>
                            )}
                            <span className="shrink-0 text-gray-400">{timeAgo(d.lastActivityAt).text}</span>
                        </button>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-gray-400">Rien à afficher pour le moment.</p>
            )}

            {selected?.model === "Event" && (
                <EventDrillDownModal eventId={selected._id} onClose={() => setSelected(null)} />
            )}
            {selected?.model === "Person" && (
                <PersonDetailModal personId={selected._id} onClose={() => setSelected(null)} />
            )}
        </div>
    );
}

function getStatusTone(status: string): string {
    if (["done", "approved"].includes(status)) return "bg-green-50 text-green-700 border-green-200";
    if (["error"].includes(status)) return "bg-red-50 text-red-700 border-red-200";
    if (["pending", "waiting_callback", "retry"].includes(status)) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-gray-50 text-gray-600 border-gray-200";
}

function ServiceCard({
    svc,
    apiUsage,
    onToggle,
    onRun,
    onWindowChange,
    onClick,
    onChartsClick,
}: {
    svc: ServiceOverviewItem;
    apiUsage: Map<string, TrackedApiService>;
    onToggle: (key: string, enabled: boolean) => Promise<void>;
    onRun: (key: string) => Promise<void>;
    onWindowChange: (key: string, days: number) => Promise<void>;
    onClick?: () => void;
    onChartsClick?: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const [running, setRunning] = useState(false);
    const [runMsg, setRunMsg] = useState<string | null>(null);
    const [editingWindow, setEditingWindow] = useState(false);
    const [windowInput, setWindowInput] = useState(String(svc.freshnessWindowDays ?? ""));
    const [expanded, setExpanded] = useState(false);

    const ago = timeAgo(svc.lastActivityAt);
    const url = logsUrl(svc.logService);

    const handleToggle = async (v: boolean) => {
        setBusy(true);
        try {
            await onToggle(svc.key, v);
        } finally {
            setBusy(false);
        }
    };

    const handleRun = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setRunning(true);
        try {
            await onRun(svc.key);
            setRunMsg("Lancé ✓");
            setTimeout(() => setRunMsg(null), 4000);
        } finally {
            setRunning(false);
        }
    };

    const saveWindow = async (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        const days = parseInt(windowInput, 10);
        if (!isNaN(days) && days >= 1) {
            await onWindowChange(svc.key, days);
        }
        setEditingWindow(false);
    };

    const eta = fmtEta(svc.metrics.etaHours, svc.metrics.etaBlocked);
    const coverage = svc.metrics.coveragePct;

    return (
        <div
            className={`border rounded-lg p-4 flex flex-col gap-3 transition-all bg-white ${
                svc.health === "stalled"
                    ? "border-red-300"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
            } ${onClick || svc.hasRecentDocs ? "cursor-pointer" : ""} ${svc.enabled ? "" : "opacity-75"}`}
            onClick={onClick ?? (svc.hasRecentDocs ? () => setExpanded((v) => !v) : undefined)}
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-900">{svc.label}</p>
                        <HealthBadge health={svc.health} />
                        {svc.isWorkerRunning === true && (
                            <span className="flex items-center gap-1 text-[11px] text-green-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> worker actif
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{svc.description}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{svc.mechanism}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {svc.canRun && (
                        <button
                            onClick={handleRun}
                            disabled={running || busy}
                            title="Lancer un cycle maintenant"
                            className="text-gray-400 hover:text-gray-700 disabled:opacity-40"
                        >
                            <Play className={`w-4 h-4 ${running ? "text-amber-500 animate-pulse" : ""}`} />
                        </button>
                    )}
                    <ToggleSwitch enabled={svc.enabled} disabled={busy || svc.retired} onChange={handleToggle} />
                </div>
            </div>

            {runMsg && <p className="text-xs text-green-600">{runMsg}</p>}

            {/* Metrics */}
            {svc.kind === "queue" ? (
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-end justify-between">
                        <div>
                            <p
                                className={`text-2xl font-semibold ${
                                    (svc.counts.eligible ?? 0) === 0 ? "text-green-600" : "text-gray-900"
                                }`}
                            >
                                {fmt(svc.counts.eligible)}
                            </p>
                            <p className="text-xs text-gray-400">
                                à traiter ({svc.targetLabel.toLowerCase()} éligibles)
                            </p>
                        </div>
                        <Sparkline points={svc.sparkline} color="#9ca3af" />
                    </div>
                    {(svc.counts.outsideLimits ?? 0) > 0 && (
                        <p className="text-xs text-amber-600">
                            + {fmt(svc.counts.outsideLimits)} hors limites (non traités) · pool total :{" "}
                            {fmt(svc.counts.pool)}
                        </p>
                    )}
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">
                            Vitesse : <span className="text-gray-700 font-medium">{fmtSpeed(svc.metrics.speedPerHour)}</span>
                        </span>
                        <span className="text-gray-500">
                            Fin estimée : <span className={`font-medium ${eta.color}`}>{eta.text}</span>
                        </span>
                    </div>
                    {svc.counts.processedToday !== null && (
                        <p className="text-xs text-gray-500">
                            Traités aujourd'hui :{" "}
                            <span className="text-gray-700 font-medium">{fmt(svc.counts.processedToday)}</span>
                        </p>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                            Couverture ({svc.freshnessWindowDays ?? "?"} j)
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingWindow(true);
                                }}
                                className="text-gray-300 hover:text-gray-600"
                                title="Modifier la fenêtre de fraîcheur"
                            >
                                <Pencil className="w-3 h-3" />
                            </button>
                        </span>
                        <span className="font-semibold text-gray-800">
                            {coverage === null ? "—" : `${coverage.toFixed(1).replace(".", ",")} %`}
                        </span>
                    </div>
                    {editingWindow && (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="number"
                                min={1}
                                value={windowInput}
                                onChange={(e) => setWindowInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && saveWindow(e)}
                                className="border border-gray-200 rounded px-2 py-0.5 text-xs w-16"
                            />
                            <span className="text-xs text-gray-400">jours</span>
                            <button onClick={saveWindow} className="text-xs text-white bg-gray-900 rounded px-2 py-0.5">
                                OK
                            </button>
                        </div>
                    )}
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${
                                (coverage ?? 0) >= 80 ? "bg-green-500" : (coverage ?? 0) >= 30 ? "bg-amber-400" : "bg-red-400"
                            }`}
                            style={{ width: `${Math.min(100, coverage ?? 0)}%` }}
                        />
                    </div>
                    <p className="text-xs text-gray-500">
                        {fmt(svc.counts.fresh)} / {fmt(svc.counts.population)} vérifiés · tour complet :{" "}
                        <span className="text-gray-700 font-medium">{fmtCycle(svc.metrics.cycleDays)}</span>
                    </p>
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">
                            Aujourd'hui :{" "}
                            <span className="text-gray-700 font-medium">{fmt(svc.counts.processedToday)}</span>
                        </span>
                        <span className="text-gray-500">
                            Vitesse : <span className="text-gray-700 font-medium">{fmtSpeed(svc.metrics.speedPerHour)}</span>
                        </span>
                    </div>
                    {(svc.counts.excluded ?? 0) > 0 && svc.excludedLabel && (
                        <p className="text-xs text-gray-400">
                            {fmt(svc.counts.excluded)} {svc.excludedLabel}
                        </p>
                    )}
                </div>
            )}

            {/* Limitations & APIs */}
            <div className="flex items-center gap-1.5 flex-wrap">
                {svc.limitations.map((l) => (
                    <Chip key={l} tone="amber">
                        {l}
                    </Chip>
                ))}
                {svc.trackedApis.map((api) => {
                    const t = apiUsage.get(api);
                    const pct =
                        t && t.keys.length > 0
                            ? Math.round(
                                  (t.keys.reduce((s, k) => s + k.currentUsage, 0) /
                                      Math.max(1, t.keys.reduce((s, k) => s + k.quotaLimit, 0))) * 100
                              )
                            : null;
                    return (
                        <Chip key={api} tone="blue">
                            <KeyRound className="w-3 h-3 mr-1" />
                            {api}
                            {pct !== null ? ` · ${pct} %` : ""}
                        </Chip>
                    );
                })}
                {svc.externalApis.map((api) => (
                    <Chip key={api} tone="blue">
                        {api}
                    </Chip>
                ))}
            </div>

            {/* Recent activity (drill-down) */}
            {svc.hasRecentDocs && (
                <div className="border-t border-gray-100 pt-2.5" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
                        Activité récente
                    </button>
                    {expanded && (
                        <div className="mt-2">
                            <RecentActivityList svcKey={svc.key} />
                        </div>
                    )}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-gray-100 pt-2.5">
                <div className="flex items-center gap-2">
                    {onChartsClick && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onChartsClick();
                            }}
                            className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 underline"
                        >
                            <LineChart className="w-3 h-3" />
                            Graphiques
                        </button>
                    )}
                    {url && (
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[11px] text-gray-400 hover:text-gray-600 underline"
                        >
                            Better Stack
                        </a>
                    )}
                    {svc.snapshotAt && (
                        <span className="text-[11px] text-gray-300" title="Âge des compteurs (snapshot)">
                            données {timeAgo(svc.snapshotAt).text}
                        </span>
                    )}
                </div>
                <p className={`text-xs font-medium ${ago.color}`} title="Dernière activité réelle du service">
                    {ago.text}
                </p>
            </div>
        </div>
    );
}

// ─── APIs panel ────────────────────────────────────────────────────────────────

function UsageBar({ pct }: { pct: number }) {
    return (
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden w-full">
            <div
                className={`h-full rounded-full ${pct >= 90 ? "bg-red-500" : pct >= 60 ? "bg-amber-400" : "bg-green-500"}`}
                style={{ width: `${Math.min(100, pct)}%` }}
            />
        </div>
    );
}

/** Dépensé / Restant / Total en USD, sur une ligne. */
function CostSummary({ cost }: { cost: { spent: number; remaining: number; total: number } }) {
    const cells: [string, string, string][] = [
        ["Dépensé", fmtUsd(cost.spent), "text-gray-900"],
        ["Restant", fmtUsd(cost.remaining), "text-green-600"],
        ["Total", fmtUsd(cost.total), "text-gray-500"],
    ];
    return (
        <div className="flex items-center gap-4">
            {cells.map(([label, value, color]) => (
                <div key={label}>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
                    <p className={`text-xs font-semibold ${color}`}>{value}</p>
                </div>
            ))}
        </div>
    );
}

// ─── Daily pacing (quota réparti par jour) ─────────────────────────────────────

const PACE_W = 520;
const PACE_H = 88;
const PACE_TOP = 6;
const PACE_BAR = "#3b82f6"; // jours passés
const PACE_BAR_TODAY = "#1e40af";
const PACE_TICK = "#111827"; // budget du jour
const PACE_EVEN = "#9ca3af"; // rythme régulier
const PACE_GHOST = "#e5e7eb"; // jours à venir

/** Colonne à extrémité arrondie (4px), carrée sur la ligne de base. */
function columnPath(x: number, y: number, w: number, h: number): string {
    const r = Math.min(4, w / 2, h);
    const base = y + h;
    return `M${x},${base}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${base}Z`;
}

function fmtDay(day: string, opts: Intl.DateTimeFormatOptions): string {
    const [y, m, d] = day.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("fr-FR", opts);
}

function pacingReadout(p: ApiKeyPacing, d: PacingDay): string {
    const date = fmtDay(d.day, { weekday: "short", day: "numeric", month: "short" });
    if (d.when === "future") return `${date} · à venir, ≈ ${fmt(p.nextDaysPace)} appels prévus`;
    if (d.when === "today")
        return `${date} (aujourd'hui) · ${fmt(d.count)} appels sur un budget de ${fmt(d.budget)}, reste ${fmt(p.remainingToday)}`;
    if (d.count === null) return `${date} · rien d'enregistré (service arrêté, ou avant le suivi)`;
    return `${date} · ${fmt(d.count)} appels${d.budget !== null ? ` sur un budget de ${fmt(d.budget)}` : ""}`;
}

function DailyPacingChart({ pacing }: { pacing: ApiKeyPacing }) {
    const [hover, setHover] = useState<number | null>(null);
    const days = pacing.days;
    const todayIdx = days.findIndex((d) => d.when === "today");
    const n = days.length;
    const slot = PACE_W / n;
    const barW = Math.min(24, slot - 2);
    // Wider than the bar where there is room, but never into the next day's slot — on a
    // 31-day month adjacent ticks would otherwise join into one line
    const tickW = Math.min(barW + 4, slot - 3);
    const max =
        Math.max(1, pacing.evenPace, pacing.nextDaysPace, ...days.map((d) => Math.max(d.count ?? 0, d.budget ?? 0))) * 1.1;
    const base = PACE_H - 1;
    const yOf = (v: number) => base - (v / max) * (base - PACE_TOP);
    const active = days[hover ?? todayIdx] ?? null;

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const from = hover ?? todayIdx;
        setHover(Math.max(0, Math.min(n - 1, from + (e.key === "ArrowRight" ? 1 : -1))));
    };

    return (
        <div>
            <svg
                viewBox={`0 0 ${PACE_W} ${PACE_H}`}
                width="100%"
                className="block outline-none focus-visible:ring-2 focus-visible:ring-blue-300 rounded"
                role="img"
                aria-label="Appels par jour sur la période (flèches gauche / droite pour parcourir les jours)"
                tabIndex={0}
                onKeyDown={onKeyDown}
                onMouseLeave={() => setHover(null)}
                onBlur={() => setHover(null)}
            >
                {hover !== null && <rect x={hover * slot} y={0} width={slot} height={PACE_H} fill="#f3f4f6" />}
                <line x1={0} x2={PACE_W} y1={base + 0.5} y2={base + 0.5} stroke="#e5e7eb" strokeWidth={1} />
                <line
                    x1={0}
                    x2={PACE_W}
                    y1={yOf(pacing.evenPace)}
                    y2={yOf(pacing.evenPace)}
                    stroke={PACE_EVEN}
                    strokeWidth={1}
                />
                {days.map((d, i) => {
                    const x = i * slot + (slot - barW) / 2;
                    const value = d.when === "future" ? pacing.nextDaysPace : d.count ?? 0;
                    const top = yOf(value);
                    return (
                        <g key={d.day}>
                            {value > 0 && (
                                <path
                                    d={columnPath(x, top, barW, base - top)}
                                    fill={d.when === "future" ? PACE_GHOST : d.when === "today" ? PACE_BAR_TODAY : PACE_BAR}
                                />
                            )}
                            {d.budget !== null && d.when !== "future" && (
                                <rect
                                    x={x + (barW - tickW) / 2}
                                    y={yOf(d.budget) - 1}
                                    width={tickW}
                                    height={2}
                                    rx={1}
                                    fill={PACE_TICK}
                                />
                            )}
                            <rect
                                x={i * slot}
                                y={0}
                                width={slot}
                                height={PACE_H}
                                fill="transparent"
                                onMouseEnter={() => setHover(i)}
                            />
                        </g>
                    );
                })}
            </svg>
            <div className="flex justify-between text-[10px] text-gray-400 mt-0.5 tabular-nums">
                <span>{fmtDay(days[0].day, { day: "numeric", month: "short" })}</span>
                <span>{fmtDay(days[n - 1].day, { day: "numeric", month: "short" })}</span>
            </div>
            <p className="text-[11px] text-gray-700 mt-1 min-h-[1rem]" aria-live="polite">
                {active ? pacingReadout(pacing, active) : ""}
            </p>
            <div className="flex items-center gap-3 flex-wrap text-[10px] text-gray-500 mt-1">
                <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2.5 rounded-t-sm" style={{ background: PACE_BAR }} />
                    appels du jour
                </span>
                <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-0.5 rounded" style={{ background: PACE_TICK }} />
                    budget du jour
                </span>
                <span className="flex items-center gap-1">
                    <span className="inline-block w-3 border-t" style={{ borderColor: PACE_EVEN }} />
                    rythme régulier ≈ {fmt(Math.round(pacing.evenPace))}/j
                </span>
                <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2.5 rounded-t-sm" style={{ background: PACE_GHOST }} />
                    à venir
                </span>
            </div>
        </div>
    );
}

function DailyPacingPanel({ k, onToggle }: { k: ApiKeyInfo; onToggle: (enabled: boolean) => Promise<void> }) {
    if (!k.pacingAvailable) return null;
    const p = k.pacing;
    const spentForToday = p !== null && p.remainingToday === 0 && p.periodRemaining > 0;

    const cells: [string, React.ReactNode][] = p
        ? [
              ["Aujourd'hui", `${fmt(p.usedToday)} / ${fmt(p.budget)}`],
              [
                  "Reste aujourd'hui",
                  spentForToday ? (
                      <span className="text-gray-500">0 · reprise demain</span>
                  ) : (
                      <span className="text-green-600">{fmt(p.remainingToday)}</span>
                  ),
              ],
              ["Reste période", `${fmt(p.periodRemaining)} sur ${p.daysLeft} j`],
              ["Ensuite", p.daysLeft > 1 ? `≈ ${fmt(p.nextDaysPace)}/j` : "—"],
          ]
        : [];

    return (
        <div className="mt-2 border border-gray-100 rounded p-2.5">
            <div className="flex items-center justify-between mb-1.5">
                <span
                    className="text-[11px] font-medium text-gray-700"
                    title="Budget du jour = quota restant ÷ jours restants dans la période. Un jour calme reporte sa part sur les suivants, un dépassement est repris sur eux."
                >
                    Répartition journalière
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                    {k.dailyPacing ? "activée" : "désactivée"}
                    <ToggleSwitch enabled={k.dailyPacing} onChange={onToggle} />
                </span>
            </div>
            {p ? (
                <>
                    <div className="flex items-start gap-4 flex-wrap mb-2">
                        {cells.map(([label, value]) => (
                            <div key={label}>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
                                <p className="text-xs font-semibold text-gray-900">{value}</p>
                            </div>
                        ))}
                    </div>
                    <DailyPacingChart pacing={p} />
                </>
            ) : (
                <p className="text-[11px] text-gray-400">
                    Pas de plafond journalier : le quota {PERIOD_LABELS[k.period] ?? ""} est consommé au fil de l'eau
                    et peut être épuisé dès les premiers jours.
                </p>
            )}
        </div>
    );
}

function ApisPanel({
    overview,
    serviceLabels,
    onKeyUpdate,
}: {
    overview: ServicesOverview;
    serviceLabels: Map<string, string>;
    onKeyUpdate: (
        serviceName: string,
        envVarName: string,
        update: { quotaLimit?: number; isActive?: boolean; costPerCall?: number | null; dailyPacing?: boolean }
    ) => Promise<void>;
}) {
    const [editing, setEditing] = useState<string | null>(null);
    const [limitInput, setLimitInput] = useState("");
    const [editingPrice, setEditingPrice] = useState<string | null>(null);
    const [priceInput, setPriceInput] = useState("");

    return (
        <div className="flex flex-col gap-6">
            {/* Tracked quotas */}
            <div>
                <p className="text-sm font-semibold text-gray-900 mb-3">Quotas suivis (rate-limiter)</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {overview.apis.tracked.map((svc) => (
                        <div key={svc.serviceName} className="border border-gray-200 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-semibold text-gray-900">{svc.serviceName}</p>
                                <div className="flex gap-1">
                                    {svc.usedBy.map((k) => (
                                        <Chip key={k}>{serviceLabels.get(k) ?? k}</Chip>
                                    ))}
                                </div>
                            </div>
                            {svc.description && <p className="text-xs text-gray-400 mb-2">{svc.description}</p>}
                            {svc.cost && (
                                <div className="flex items-center justify-between border-y border-gray-100 py-2 mb-3">
                                    <CostSummary cost={svc.cost} />
                                    {svc.cost.perCall !== undefined && (
                                        <span className="text-[11px] text-gray-400 whitespace-nowrap">
                                            {fmtUnitUsd(svc.cost.perCall)} / appel
                                        </span>
                                    )}
                                </div>
                            )}
                            {svc.keys.map((k) => {
                                const editKey = `${svc.serviceName}:${k.envVarName}`;
                                return (
                                    <div key={k.envVarName} className="mb-3 last:mb-0">
                                        <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="font-mono text-gray-600">{k.envVarName}</span>
                                            <span className="flex items-center gap-2">
                                                {editing === editKey ? (
                                                    <span className="flex items-center gap-1">
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            value={limitInput}
                                                            onChange={(e) => setLimitInput(e.target.value)}
                                                            className="border border-gray-200 rounded px-1.5 py-0.5 text-xs w-20"
                                                        />
                                                        <button
                                                            onClick={async () => {
                                                                const v = parseInt(limitInput, 10);
                                                                if (!isNaN(v) && v >= 0) {
                                                                    await onKeyUpdate(svc.serviceName, k.envVarName, { quotaLimit: v });
                                                                }
                                                                setEditing(null);
                                                            }}
                                                            className="text-white bg-gray-900 rounded px-1.5 py-0.5 text-xs"
                                                        >
                                                            OK
                                                        </button>
                                                    </span>
                                                ) : (
                                                    <>
                                                        <span className="text-gray-500">
                                                            {fmt(k.currentUsage)} / {fmt(k.quotaLimit)}{" "}
                                                            <span className="text-gray-400">{PERIOD_LABELS[k.period] ?? k.period}</span>
                                                        </span>
                                                        <button
                                                            onClick={() => {
                                                                setEditing(editKey);
                                                                setLimitInput(String(k.quotaLimit));
                                                            }}
                                                            title="Modifier la limite"
                                                            className="text-gray-300 hover:text-gray-600"
                                                        >
                                                            <Pencil className="w-3 h-3" />
                                                        </button>
                                                        <ToggleSwitch
                                                            enabled={k.isActive}
                                                            onChange={(v) => onKeyUpdate(svc.serviceName, k.envVarName, { isActive: v })}
                                                        />
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                        <UsageBar pct={k.usagePercentage} />
                                        <div className="flex items-center justify-between gap-2 text-[11px] mt-1">
                                            {k.cost ? (
                                                <span className="text-gray-400">
                                                    <span className="text-gray-900 font-medium">{fmtUsd(k.cost.spent)}</span>{" "}
                                                    dépensés ·{" "}
                                                    <span className="text-green-600 font-medium">
                                                        {fmtUsd(k.cost.remaining)}
                                                    </span>{" "}
                                                    restants · {fmtUsd(k.cost.total)} au total
                                                </span>
                                            ) : (
                                                <span className="text-gray-300">Prix non renseigné</span>
                                            )}
                                            {editingPrice === editKey ? (
                                                <span className="flex items-center gap-1">
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={0.0001}
                                                        autoFocus
                                                        placeholder="0,0000"
                                                        value={priceInput}
                                                        onChange={(e) => setPriceInput(e.target.value)}
                                                        className="border border-gray-200 rounded px-1.5 py-0.5 text-[11px] w-24"
                                                    />
                                                    <span className="text-gray-400">$ / appel</span>
                                                    <button
                                                        onClick={async () => {
                                                            const raw = priceInput.trim().replace(",", ".");
                                                            const v = raw === "" ? null : Number(raw);
                                                            if (v === null || (isFinite(v) && v >= 0)) {
                                                                await onKeyUpdate(svc.serviceName, k.envVarName, {
                                                                    costPerCall: v,
                                                                });
                                                            }
                                                            setEditingPrice(null);
                                                        }}
                                                        className="text-white bg-gray-900 rounded px-1.5 py-0.5 text-[11px]"
                                                    >
                                                        OK
                                                    </button>
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => {
                                                        setEditingPrice(editKey);
                                                        setPriceInput(k.costPerCall === null ? "" : String(k.costPerCall));
                                                    }}
                                                    title={
                                                        k.costIsCustom
                                                            ? "Prix propre à cette clé — vider le champ pour revenir au tarif du service"
                                                            : "Définir le prix d'un appel pour cette clé"
                                                    }
                                                    className="flex items-center gap-1 text-gray-400 hover:text-gray-700 whitespace-nowrap"
                                                >
                                                    {k.costPerCall === null
                                                        ? "définir le prix"
                                                        : `${fmtUnitUsd(k.costPerCall)} / appel`}
                                                    {k.costIsCustom && <span className="text-blue-400">•</span>}
                                                    <Pencil className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                        {k.isSaturated && <p className="text-[11px] text-red-500 mt-0.5">Quota épuisé</p>}
                                        <DailyPacingPanel
                                            k={k}
                                            onToggle={(v) => onKeyUpdate(svc.serviceName, k.envVarName, { dailyPacing: v })}
                                        />
                                    </div>
                                );
                            })}
                            {svc.keys.length === 0 && <p className="text-xs text-gray-400">Aucune clé configurée</p>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Shared keys split */}
            {overview.apis.sharedKeys.length > 0 && (
                <div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">Clés partagées entre services</p>
                    <p className="text-xs text-gray-400 mb-3">
                        Une même clé physique utilisée par plusieurs services — répartition de la consommation réelle.
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {overview.apis.sharedKeys.map((sk) => (
                            <div key={sk.envVarName} className="border border-gray-200 rounded-lg p-4 bg-white">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-mono text-xs text-gray-700">{sk.envVarName}</span>
                                    <span className="text-xs text-gray-500">{fmt(sk.totalUsage)} appels au total</span>
                                </div>
                                {sk.entries.map((e) => (
                                    <div key={e.serviceName} className="flex items-center gap-2 text-xs mb-1.5">
                                        <span className="w-36 truncate text-gray-600">{e.serviceName}</span>
                                        <div className="flex-1">
                                            <UsageBar pct={e.shareOfKeyPct} />
                                        </div>
                                        <span className="w-24 text-right text-gray-500">
                                            {fmt(e.currentUsage)} ({Math.round(e.shareOfKeyPct)} %)
                                        </span>
                                        <span className="w-28 text-right text-gray-400" title="Dépensé / budget de la période">
                                            {e.cost ? `${fmtUsd(e.cost.spent)} / ${fmtUsd(e.cost.total)}` : "—"}
                                        </span>
                                    </div>
                                ))}
                                {sk.cost && (
                                    <div className="border-t border-gray-100 mt-2 pt-2">
                                        <CostSummary cost={sk.cost} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* External APIs */}
            <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">APIs externes non suivies</p>
                <p className="text-xs text-gray-400 mb-3">
                    Ces APIs n'ont pas de compteur de quota en base — limites connues et services consommateurs.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {overview.apis.external.map((api) => (
                        <div key={api.name} className="border border-gray-200 rounded-lg p-4 bg-white">
                            <p className="text-sm font-semibold text-gray-900 mb-1">{api.name}</p>
                            <p className="text-xs text-gray-500 mb-2">{api.note}</p>
                            <div className="flex gap-1 flex-wrap">
                                {api.services.map((k) => (
                                    <Chip key={k}>{serviceLabels.get(k) ?? k}</Chip>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─── Migrations panel ──────────────────────────────────────────────────────────

function MigrationsPanel() {
    const [migrations, setMigrations] = useState<MigrationInfo[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getMigrations()
            .then(setMigrations)
            .catch(() => setError("Impossible de charger les migrations."));
    }, []);

    if (error) return <p className="text-sm text-red-600">{error}</p>;
    if (!migrations) return <p className="text-sm text-gray-500">Chargement…</p>;

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Migrations au démarrage</p>
                <p className="text-xs text-gray-400">
                    Exécutées séquentiellement à chaque démarrage du serveur d'enrichissement
                </p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Migration</th>
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">État</th>
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Dernière exécution</th>
                            <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Durée</th>
                            <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Résultat</th>
                            <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Exécutions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {migrations.map((m) => {
                            const ago = m.lastRun ? timeAgo(m.lastRun.startedAt) : null;
                            return (
                                <tr key={m.name} className="border-b border-gray-50">
                                    <td className="px-4 py-2 font-mono text-xs text-gray-800">{m.name}</td>
                                    <td className="px-4 py-2">
                                        <span
                                            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${
                                                m.active
                                                    ? "bg-green-50 text-green-700 border-green-200"
                                                    : "bg-gray-100 text-gray-500 border-gray-200"
                                            }`}
                                        >
                                            {m.active ? "Active" : "Désactivée"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-xs">
                                        {ago ? (
                                            <span className={ago.color}>{ago.text}</span>
                                        ) : (
                                            <span className="text-gray-400">jamais tracée</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right text-xs text-gray-500">
                                        {fmtDuration(m.lastRun?.durationMs ?? null)}
                                    </td>
                                    <td className="px-4 py-2 text-xs">
                                        {m.lastRun ? (
                                            m.lastRun.status === "success" ? (
                                                <span className="text-green-600">OK</span>
                                            ) : m.lastRun.status === "running" ? (
                                                <span className="text-amber-600">
                                                    En cours…
                                                    {m.lastRun.progress && m.lastRun.progress.total > 0 ? (
                                                        <span className="text-gray-400">
                                                            {" "}
                                                            ({m.lastRun.progress.processed}/{m.lastRun.progress.total} —{" "}
                                                            {Math.round((m.lastRun.progress.processed / m.lastRun.progress.total) * 100)}%)
                                                        </span>
                                                    ) : null}
                                                </span>
                                            ) : (
                                                <span className="text-red-600" title={m.lastRun.error ?? undefined}>
                                                    Erreur{m.lastRun.error ? ` — ${m.lastRun.error.slice(0, 60)}` : ""}
                                                </span>
                                            )
                                        ) : (
                                            <span className="text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right text-xs text-gray-500">{m.runCount}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

type Tab = "services" | "apis" | "migrations";
type HealthFilter = "all" | "running" | "stalled" | "disabled";

export default function ServicesMonitoring() {
    const navigate = useNavigate();
    const [overview, setOverview] = useState<ServicesOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [snapshotting, setSnapshotting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTabRaw] = useState<Tab>(() => (localStorage.getItem("sm_tab") as Tab) ?? "services");
    const [healthFilter, setHealthFilterRaw] = useState<HealthFilter>(
        () => (localStorage.getItem("sm_health") as HealthFilter) ?? "all"
    );
    const [editingWindow, setEditingWindow] = useState(false);
    const [windowInput, setWindowInput] = useState("");
    const loadingRef = useRef(false);

    const setTab = (t: Tab) => {
        setTabRaw(t);
        localStorage.setItem("sm_tab", t);
    };
    const setHealthFilter = (f: HealthFilter) => {
        setHealthFilterRaw(f);
        localStorage.setItem("sm_health", f);
    };

    const load = useCallback(async (showSpinner = true) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        if (showSpinner) setLoading(true);
        setError(null);
        try {
            setOverview(await getServicesOverview());
        } catch {
            setError("Impossible de charger l'état des services.");
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(() => load(false), 60_000);
        return () => clearInterval(id);
    }, [load]);

    const refreshData = async () => {
        setSnapshotting(true);
        try {
            const requestedAt = Date.now();
            const { queued } = await forceServicesSnapshot();
            if (queued) {
                // Taken by the worker, not by this request: wait until a snapshot newer than the
                // click shows up (the worker polls every 15 s, a full pass takes a little longer).
                for (let i = 0; i < 24; i++) {
                    await new Promise((r) => setTimeout(r, 5_000));
                    const next = await getServicesOverview();
                    const newest = Math.max(0, ...next.services.map((sv) => (sv.snapshotAt ? new Date(sv.snapshotAt).getTime() : 0)));
                    if (newest > requestedAt) break;
                }
            }
            await load(false);
        } catch {
            setError("Échec du recalcul des compteurs.");
        } finally {
            setSnapshotting(false);
        }
    };

    const handleToggle = async (key: string, enabled: boolean) => {
        await toggleService(key, enabled);
        await load(false);
    };

    const handleRun = async (key: string) => {
        await runService(key);
    };

    const handleWindowChange = async (key: string, days: number) => {
        await updateServiceSettings(key, { freshnessWindowDays: days });
        await refreshData();
    };

    const handleKeyUpdate = async (
        serviceName: string,
        envVarName: string,
        update: { quotaLimit?: number; isActive?: boolean; costPerCall?: number | null; dailyPacing?: boolean }
    ) => {
        await updateApiKey(serviceName, envVarName, update);
        await load(false);
    };

    const saveGlobalWindow = async () => {
        const raw = windowInput.trim();
        const value = raw === "" || raw === "-1" ? null : parseInt(raw, 10);
        if (value !== null && (isNaN(value) || value < 1)) return;
        await updateGlobalServiceSettings({ enrichmentLimitationDays: value });
        setEditingWindow(false);
        await refreshData();
    };

    const serviceLabels = new Map<string, string>(
        (overview?.services ?? []).map((s) => [s.key, s.label])
    );
    const apiUsage = new Map<string, TrackedApiService>(
        (overview?.apis.tracked ?? []).map((t) => [t.serviceName, t])
    );

    const services = overview?.services ?? [];
    const filtered = services.filter((s) => {
        if (healthFilter === "running") return s.health === "running" || s.health === "idle";
        if (healthFilter === "stalled") return s.health === "stalled";
        if (healthFilter === "disabled") return s.health === "disabled" || s.health === "retired";
        return true;
    });
    const sweeps = filtered.filter((s) => s.kind === "sweep");
    const queues = filtered.filter((s) => s.kind === "queue");

    const healthCount = (f: HealthFilter) =>
        services.filter((s) => {
            if (f === "running") return s.health === "running" || s.health === "idle";
            if (f === "stalled") return s.health === "stalled";
            if (f === "disabled") return s.health === "disabled" || s.health === "retired";
            return true;
        }).length;

    const oldestSnapshot = services.reduce<string | null>((oldest, s) => {
        if (!s.snapshotAt) return oldest;
        return !oldest || s.snapshotAt < oldest ? s.snapshotAt : oldest;
    }, null);

    const enrichmentDays = overview?.settings.enrichmentLimitationDays ?? null;

    const cardFor = (svc: ServiceOverviewItem) => (
        <ServiceCard
            key={svc.key}
            svc={svc}
            apiUsage={apiUsage}
            onToggle={handleToggle}
            onRun={handleRun}
            onWindowChange={handleWindowChange}
            onChartsClick={DETAIL_TYPE[svc.key] ? () => navigate(`/bo/services/${DETAIL_TYPE[svc.key]}`) : undefined}
            onClick={
                svc.externalDetailUrl
                    ? () => navigate(svc.externalDetailUrl!)
                    : !svc.hasRecentDocs && DETAIL_TYPE[svc.key]
                        ? () => navigate(`/bo/services/${DETAIL_TYPE[svc.key]}`)
                        : undefined
            }
        />
    );

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6">
                <BONavigation />

                {/* Header */}
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <h1 className="text-xl font-semibold text-gray-900">Services</h1>
                        <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-0.5 bg-white">
                            {([
                                { id: "services", label: "Services", icon: Activity },
                                { id: "apis", label: "APIs & Quotas", icon: KeyRound },
                                { id: "migrations", label: "Migrations", icon: Database },
                            ] as { id: Tab; label: string; icon: any }[]).map(({ id, label, icon: Icon }) => (
                                <button
                                    key={id}
                                    onClick={() => setTab(id)}
                                    className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${
                                        tab === id ? "bg-gray-900 text-white" : "text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    {label}
                                </button>
                            ))}
                        </div>
                        {tab === "services" && (
                            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-0.5 bg-white">
                                {([
                                    { id: "all", label: `Tous (${healthCount("all")})` },
                                    { id: "running", label: `Actifs (${healthCount("running")})` },
                                    { id: "stalled", label: `Bloqués (${healthCount("stalled")})` },
                                    { id: "disabled", label: `Inactifs (${healthCount("disabled")})` },
                                ] as { id: HealthFilter; label: string }[]).map(({ id, label }) => (
                                    <button
                                        key={id}
                                        onClick={() => setHealthFilter(id)}
                                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                            healthFilter === id
                                                ? id === "stalled"
                                                    ? "bg-red-600 text-white"
                                                    : "bg-gray-900 text-white"
                                                : "text-gray-500 hover:text-gray-700"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        {oldestSnapshot && (
                            <span className="text-xs text-gray-400" title="Âge des compteurs les plus anciens">
                                <Timer className="w-3.5 h-3.5 inline mr-1" />
                                données {timeAgo(oldestSnapshot).text}
                            </span>
                        )}
                        <button
                            onClick={refreshData}
                            disabled={snapshotting}
                            title="Recompter maintenant (requêtes lourdes en base)"
                            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${snapshotting ? "animate-spin" : ""}`} />
                            {snapshotting ? "Recalcul…" : "Recalculer"}
                        </button>
                    </div>
                </div>

                {/* Global settings bar */}
                {overview && tab === "services" && (
                    <div className="border border-gray-200 rounded-lg px-4 py-2.5 mb-4 bg-white flex items-center gap-4 flex-wrap">
                        <span className="text-xs font-medium text-gray-700">Fenêtre d'enrichissement globale :</span>
                        {editingWindow ? (
                            <span className="flex items-center gap-1.5">
                                <input
                                    type="number"
                                    value={windowInput}
                                    onChange={(e) => setWindowInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && saveGlobalWindow()}
                                    placeholder="-1 = aucune"
                                    className="border border-gray-200 rounded px-2 py-0.5 text-xs w-24"
                                />
                                <span className="text-xs text-gray-400">jours (-1 ou vide = aucune limite)</span>
                                <button onClick={saveGlobalWindow} className="text-xs text-white bg-gray-900 rounded px-2 py-0.5">
                                    Enregistrer
                                </button>
                                <button onClick={() => setEditingWindow(false)} className="text-xs text-gray-400">
                                    Annuler
                                </button>
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-900 font-semibold">
                                    {enrichmentDays === null
                                        ? "Aucune — tout l'historique est traité"
                                        : `${enrichmentDays} derniers jours + événements futurs`}
                                </span>
                                <button
                                    onClick={() => {
                                        setWindowInput(enrichmentDays === null ? "" : String(enrichmentDays));
                                        setEditingWindow(true);
                                    }}
                                    className="text-gray-300 hover:text-gray-600"
                                    title="Modifier"
                                >
                                    <Pencil className="w-3.5 h-3.5" />
                                </button>
                            </span>
                        )}
                        <span className="text-[11px] text-gray-400">
                            S'applique aux services marqués « Fenêtre d'enrichissement » — les documents hors fenêtre
                            restent en base mais ne sont pas traités.
                        </span>
                    </div>
                )}

                {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                {loading && !overview && <p className="text-sm text-gray-500">Chargement…</p>}

                {overview && tab === "services" && (
                    <div className="flex flex-col gap-6">
                        {sweeps.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                                    Balayages continus — couverture de la base
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {sweeps.map(cardFor)}
                                </div>
                            </div>
                        )}
                        {queues.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                                    Files de traitement — vidables
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {queues.map(cardFor)}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {overview && tab === "apis" && (
                    <ApisPanel overview={overview} serviceLabels={serviceLabels} onKeyUpdate={handleKeyUpdate} />
                )}

                {tab === "migrations" && <MigrationsPanel />}
            </div>
        </div>
    );
}
